import { LayoutList, Maximize2, Table2, X } from "lucide-react";
import {
  Children,
  isValidElement,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

interface TableData {
  headers: ReactNode[];
  headerLabels: string[];
  rows: ReactNode[][];
}

export function ResponsiveTable({ children }: { children: ReactNode }) {
  const data = useMemo(() => extractTableData(children), [children]);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"cards" | "table">(() =>
    window.matchMedia("(max-width: 679px)").matches ? "cards" : "table"
  );

  useEffect(() => {
    if (!open) return;
    const close = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [open]);

  const summary = `${data.rows.length} ligne${data.rows.length > 1 ? "s" : ""} · ${data.headers.length} colonne${data.headers.length > 1 ? "s" : ""}`;
  return (
    <>
      <div className="markdown-table-shell">
        <div className="markdown-table-toolbar">
          <span><Table2 aria-hidden="true" />{summary}</span>
          <button type="button" onClick={() => setOpen(true)}><Maximize2 aria-hidden="true" />Agrandir</button>
        </div>
        <div className="markdown-table-scroll"><table>{children}</table></div>
        <div className="markdown-table-card-preview">
          {data.rows.slice(0, 2).map((row, index) => <TableCard data={data} row={row} index={index} compact key={index} />)}
          {data.rows.length > 2 ? <small>+ {data.rows.length - 2} autres lignes</small> : null}
        </div>
      </div>
      {open ? createPortal(
        <div className="table-viewer-layer">
          <button className="table-viewer-backdrop" type="button" onClick={() => setOpen(false)} aria-label="Fermer le tableau" />
          <section className="table-viewer" role="dialog" aria-modal="true" aria-label="Tableau en plein écran">
            <header className="table-viewer-heading">
              <span><Table2 aria-hidden="true" /><span><strong>Tableau</strong><small>{summary}</small></span></span>
              <div className="table-viewer-actions">
                <div className="table-viewer-modes" role="tablist" aria-label="Mode d’affichage du tableau">
                  <button className={mode === "cards" ? "active" : ""} type="button" role="tab" aria-selected={mode === "cards"} onClick={() => setMode("cards")}><LayoutList aria-hidden="true" />Cartes</button>
                  <button className={mode === "table" ? "active" : ""} type="button" role="tab" aria-selected={mode === "table"} onClick={() => setMode("table")}><Table2 aria-hidden="true" />Tableau</button>
                </div>
                <button className="icon-command" type="button" onClick={() => setOpen(false)} aria-label="Fermer" title="Fermer"><X aria-hidden="true" /></button>
              </div>
            </header>
            <div className={`table-viewer-content ${mode}`}>
              {mode === "cards" ? (
                <div className="table-card-list">
                  {data.rows.map((row, index) => <TableCard data={data} row={row} index={index} key={index} />)}
                </div>
              ) : (
                <div className="table-viewer-grid"><table>{children}</table></div>
              )}
            </div>
          </section>
        </div>,
        document.body
      ) : null}
    </>
  );
}

function TableCard(props: { data: TableData; row: ReactNode[]; index: number; compact?: boolean }) {
  return (
    <article className={`table-card ${props.compact ? "compact" : ""}`}>
      <header>Ligne {props.index + 1}</header>
      <dl>
        {props.data.headerLabels.map((label, column) => (
          <div key={`${column}:${label}`}>
            <dt>{label || `Colonne ${column + 1}`}</dt>
            <dd>{props.row[column] ?? <span className="table-empty-value">Non renseigné</span>}</dd>
          </div>
        ))}
      </dl>
    </article>
  );
}

function extractTableData(children: ReactNode): TableData {
  const rows = findElements(children, "tr");
  const headerRow = rows.find((row) => childCells(row, "th").length > 0);
  const headers = headerRow ? childCells(headerRow, "th").map((cell) => cell.props.children) : [];
  const bodyRows = rows
    .map((row) => childCells(row, "td").map((cell) => cell.props.children))
    .filter((row) => row.length > 0);
  const columnCount = Math.max(headers.length, ...bodyRows.map((row) => row.length), 0);
  const normalizedHeaders = Array.from({ length: columnCount }, (_, index) => headers[index] ?? `Colonne ${index + 1}`);
  return {
    headers: normalizedHeaders,
    headerLabels: normalizedHeaders.map(nodeText),
    rows: bodyRows,
  };
}

function findElements(node: ReactNode, type: string): ReactElement<{ children?: ReactNode }>[] {
  const matches: ReactElement<{ children?: ReactNode }>[] = [];
  Children.forEach(node, (child) => {
    if (!isValidElement<{ children?: ReactNode }>(child)) return;
    if (child.type === type) matches.push(child);
    else matches.push(...findElements(child.props.children, type));
  });
  return matches;
}

function childCells(row: ReactElement<{ children?: ReactNode }>, type: "th" | "td") {
  return Children.toArray(row.props.children).filter(
    (child): child is ReactElement<{ children?: ReactNode }> => isValidElement<{ children?: ReactNode }>(child) && child.type === type
  );
}

function nodeText(node: ReactNode): string {
  if (typeof node === "string" || typeof node === "number") return String(node).trim();
  if (Array.isArray(node)) return node.map(nodeText).join(" ").replace(/\s+/g, " ").trim();
  if (isValidElement<{ children?: ReactNode }>(node)) return nodeText(node.props.children);
  return "";
}
